import React, { useState } from "react";
import axios from "axios";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { TESTIDS } from "@/constants/testIds";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JournalPage = () => {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email, language: lang });
      setStatus("success");
      setMsg(pick(t.footer.newsletter.success, lang));
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus("already");
        setMsg(pick(t.footer.newsletter.already, lang));
      } else {
        setStatus("error");
        setMsg(pick(t.contact.form.error, lang));
      }
    }
  };

  return (
    <section
      data-testid={TESTIDS.journalHero}
      className="relative px-6 md:px-12 lg:px-24 pt-6 md:pt-12 pb-32 md:pb-48 overflow-hidden min-h-[80vh]"
    >
      <PeonyMark size={780} opacity={0.05} className="absolute -right-44 -top-32 pointer-events-none" />
      <PeonyMark size={520} opacity={0.04} className="absolute -left-32 bottom-10 pointer-events-none" />

      <div className="grid grid-cols-12 gap-6 lg:gap-12">
        <div className="col-span-12 lg:col-span-8 lg:col-start-3">
          <Reveal>
            <p className="eyebrow text-center">{pick(t.journal.eyebrow, lang)}</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display italic text-[clamp(2.4rem,7vw,6rem)] leading-[1.02] text-depth mt-10 text-center">
              {pick(t.journal.title, lang)}
            </h1>
          </Reveal>
          <Reveal delay={280}>
            <p className="body-lux-lg mt-12 text-center max-w-2xl mx-auto">
              {pick(t.journal.body, lang)}
            </p>
          </Reveal>

          <Reveal delay={360}>
            <p className="mt-6 text-center font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere/70">
              {lang === "de" ? "Demnächst · ca. 4 min Lesezeit pro Brief" : "Coming soon · ~4 min read per letter"}
            </p>
          </Reveal>

          <Reveal delay={420}>
            <form onSubmit={submit} className="mt-20 max-w-xl mx-auto border-b border-depth/30 flex items-end gap-4 pb-3 focus-within:border-depth transition-colors">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={pick(t.footer.newsletter.placeholder, lang)}
                data-testid={TESTIDS.journalNotify}
                className="bg-transparent outline-none border-none w-full text-depth placeholder:text-warmth font-light text-lg py-2"
              />
              <button
                type="submit"
                disabled={status === "sending"}
                className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-depth hover:text-signal transition-colors py-2 disabled:opacity-50"
              >
                {status === "sending" ? "…" : pick(t.journal.notify, lang)} ↗
              </button>
            </form>
            {status !== "idle" && status !== "sending" && (
              <p className={`mt-6 text-center text-sm font-light ${status === "error" ? "text-signal" : "text-atmosphere"}`}>
                {msg}
              </p>
            )}
          </Reveal>

          <Reveal delay={600}>
            <p className="font-signature text-3xl text-atmosphere mt-20 text-center">
              avec amour, Patrice
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default JournalPage;
